import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import heroTeachersImage from '../assets/images/hero-teachers.png';
import { BRAND_FULL_NAME, BRAND_NAME, BRAND_TAGLINE, BRAND_TEACHER_NAME } from '../config/brand';

// Import UnitCodePopup
import UnitCodePopup from '../components/common/UnitCodePopup';
import WhyChooseBanner from '../components/home/WhyChooseBanner';
import SuggestedCoursesSection from '../components/home/SuggestedCoursesSection';
import TeachersSection from '../components/home/TeachersSection';
import HomeCtaBanner from '../components/home/HomeCtaBanner';
import HomeFaq from '../components/home/HomeFaq';
import RepublicToppersSection from '../components/home/RepublicToppersSection';
import Students2027RegisterPopup from '../components/home/Students2027RegisterPopup';
import SupportTelegramFab from '../components/common/SupportTelegramFab';
import { ArrowLeft, BookOpen, LayoutDashboard } from 'lucide-react';

const Home: React.FC = () => {
  const { isAuthenticated, currentUser, hasEnrolledCourses, checkEnrolledCourses } = useAuth();
  const [isUnitCodePopupOpen, setIsUnitCodePopupOpen] = useState(false);
  const isCheckingEnrollment = isAuthenticated && hasEnrolledCourses === null;
  
  // Fix hydration mismatch: only render auth-dependent content after mount
  const [mounted, setMounted] = useState(false);
  
  useEffect(() => {
    setMounted(true);
  }, []);

  // Trigger enrolled-courses check when authenticated (uses AuthContext cache; redirect handled by App NavigationHandler)
  useEffect(() => {
    if (isAuthenticated && currentUser?.id && hasEnrolledCourses === null) {
      checkEnrolledCourses();
    }
  }, [isAuthenticated, currentUser?.id, hasEnrolledCourses, checkEnrolledCourses]);

  // Show loading state while checking enrollment
  if (isCheckingEnrollment) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-700"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-paper" dir="rtl">

      <section
        className="nahj-hero nahj-hero--ready relative w-full overflow-hidden bg-green-900 lg:h-[100svh]"
        aria-labelledby="nahj-hero-heading"
      >
        <div className="pointer-events-none relative mx-auto aspect-[3/4] w-full min-h-[100svh] overflow-hidden lg:absolute lg:inset-0 lg:mx-0 lg:aspect-auto lg:h-full lg:min-h-0">
          <img
            src={heroTeachersImage}
            alt={`${BRAND_FULL_NAME} مع الشيخ أمين خليل والأستاذ سعيد الفقي - تعليم أزهري أونلاين للثانوية الأزهرية`}
            className="nahj-hero__bg absolute inset-0 h-full w-full object-cover"
            fetchPriority="high"
          />
          <div className="nahj-hero__scrim absolute inset-x-0 bottom-0 h-[55%] bg-gradient-to-t from-green-900/95 via-green-900/70 to-transparent lg:hidden" aria-hidden />
          <div className="nahj-hero__desk-wash absolute inset-y-0 right-0 hidden w-[58%] lg:block" aria-hidden />
        </div>

        <div className="absolute inset-x-0 bottom-0 z-10 mx-auto w-full max-w-[1680px] px-4 pb-8 pt-6 sm:px-6 sm:pb-10 sm:pt-8 lg:static lg:flex lg:h-full lg:items-stretch lg:px-10 lg:pb-0 lg:pt-0 xl:px-12">
          <div className="nahj-hero__content w-full max-w-xl lg:absolute lg:right-[5vw] lg:top-[22%] lg:max-w-[min(42vw,34rem)] xl:right-[6vw] xl:max-w-[36rem]">
            <h1
              id="nahj-hero-heading"
              className="nahj-hero__reveal nahj-hero__reveal--1 flex flex-col gap-1 sm:gap-2.5"
            >
              <span className="font-thmanyah block text-[clamp(1.15rem,1.2vw+0.8rem,1.75rem)] font-extrabold leading-snug tracking-wide text-emerald-200 drop-shadow-[0_3px_12px_rgba(0,0,0,0.7)]">
                {BRAND_TAGLINE}
              </span>
              <span className="font-thmanyah block text-[clamp(2.5rem,4.5vw+1rem,5rem)] font-black leading-[1.15] text-white drop-shadow-[0_3px_16px_rgba(0,0,0,0.8)]">
                {BRAND_NAME}
              </span>
            </h1>
            <p className="nahj-hero__desc nahj-hero__reveal nahj-hero__reveal--2 mt-3 max-w-[38rem] text-[clamp(1rem,1.1vw+0.75rem,1.35rem)] font-semibold leading-[1.7] text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)] sm:mt-6 sm:leading-[1.85]">
              كورسات أونلاين لطلاب الثانوية الأزهرية في اللغة العربية والعلم الشرعي، مع الأستاذ {BRAND_TEACHER_NAME}. سجّل وابدأ دلوقتي.
            </p>
            <div className="nahj-hero__reveal nahj-hero__reveal--3 mt-4 flex w-full max-w-md flex-col gap-2.5 sm:mt-7 sm:max-w-lg sm:flex-row sm:items-stretch sm:gap-3.5">
              {mounted && isAuthenticated ? (
                <Link
                  to="/student-portal"
                  className="inline-flex min-h-[52px] w-full items-center justify-center gap-2.5 rounded-[18px] border-2 border-gold-400 bg-gold-500 px-7 py-3.5 font-thmanyah text-base font-bold text-green-950 transition-colors hover:border-gold-300 hover:bg-gold-400 sm:min-w-[11rem] sm:flex-1"
                >
                  البوابة
                  <LayoutDashboard className="h-[22px] w-[22px] shrink-0" aria-hidden />
                </Link>
              ) : (
                <Link
                  to="/register"
                  className="inline-flex min-h-[52px] w-full items-center justify-center gap-2.5 rounded-[18px] border-2 border-gold-400 bg-gold-500 px-7 py-3.5 font-thmanyah text-base font-bold text-green-950 transition-colors hover:border-gold-300 hover:bg-gold-400 sm:min-w-[11rem] sm:flex-1"
                >
                  سجّل الآن
                  <ArrowLeft className="h-[22px] w-[22px] shrink-0" aria-hidden />
                </Link>
              )}
              <button
                type="button"
                onClick={() =>
                  document.getElementById('suggested-courses-section')?.scrollIntoView({ behavior: 'smooth' })
                }
                className="inline-flex min-h-[52px] w-full items-center justify-center gap-2.5 rounded-[18px] border-2 border-white/55 bg-transparent px-7 py-3.5 font-thmanyah text-base font-bold text-white transition-colors hover:border-white hover:bg-white/10 sm:min-w-[11rem] sm:flex-1"
              >
                <BookOpen className="h-[22px] w-[22px] shrink-0" aria-hidden />
                الكورسات
              </button>
            </div>
          </div>
        </div>
      </section>

      <SuggestedCoursesSection />

      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20 lg:px-8 lg:py-24">
        <TeachersSection embedded compact />
      </div>

      <WhyChooseBanner />

      <HomeCtaBanner />

      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20 lg:px-8 lg:py-24">
        <RepublicToppersSection />
      </div>

      <HomeFaq />

      <SupportTelegramFab />

      {/* Unit Code Popup */}
      <UnitCodePopup
        isOpen={isUnitCodePopupOpen}
        onClose={() => setIsUnitCodePopupOpen(false)}
      />

      {mounted && !isAuthenticated && <Students2027RegisterPopup enabled />}

    </div>
  );
};

export default React.memo(Home);
